// P20a — Page /stocks/familles : liste CRUD familles d'articles
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Trash2, Plus } from "lucide-react";
import { useCan } from "@/hooks/useCan";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";
interface FamilleArticle {
  id: number;
  name: string;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const famillesApi = {
  list: () => fetchJson<FamilleArticle[]>(`/v1/famille-articles`),
  create: (data: { name: string }) =>
    fetchJson<FamilleArticle>(`/v1/famille-articles`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id: number, data: { name: string }) =>
    fetchJson<FamilleArticle>(`/v1/famille-articles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  remove: (id: number) => fetchJson<void>(`/v1/famille-articles/${id}`, { method: "DELETE" }),
};

function FamilleModal({
  onClose,
  initial,
}: {
  onClose: () => void;
  initial?: FamilleArticle;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const data = { name: name.trim() };
      return initial ? famillesApi.update(initial.id, data) : famillesApi.create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["famille-articles"] });
      qc.invalidateQueries({ queryKey: ["articles"] });
      onClose();
      toast({
        title: initial ? "Famille modifiée" : "Famille créée",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(24rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{initial ? "Modifier" : "Nouvelle"} famille</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <label className="text-xs">{ht("Nom *")}</label>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={ht("Ex: Pneus, Filtres, Lubrifiants")}
              autoFocus
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!name.trim() || mutation.isPending}
            >
              {mutation.isPending ? "En cours..." : initial ? "Modifier" : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ListeFamillesPage() {
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<FamilleArticle | null>(null);
  const [del, setDel] = useState<FamilleArticle | null>(null);
    const { toast } = useToast();
  const qc = useQueryClient();
  const canEdit = useCan("write");

  const { data: familles, isLoading } = useQuery({
    queryKey: ["famille-articles"],
    queryFn: () => famillesApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => famillesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["famille-articles"] });
      qc.invalidateQueries({ queryKey: ["articles"] });
      toast({ title: ht("Famille supprimée"), variant: "success" });
      setDel(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({
        title,
        description: msg,
        variant: "error",
        duration: 8000,
      });
      setDel(null);
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Familles d'articles")}</h1>
          <p className="text-muted-foreground">
            Catégorisation du stock ({familles?.length ?? 0} familles)
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Nouvelle famille
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{ht("Liste des familles")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{ht("Chargement...")}</div>
          ) : !familles || familles.length === 0 ? (
            <div className="text-sm text-muted-foreground">{ht("Aucune famille enregistrée.")}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {familles.map((f) => (
                <div
                  key={f.id}
                  className="group flex items-center justify-between rounded-md border border-border p-2 hover:bg-muted/30"
                >
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">#{f.id}</div>
                    <div className="font-semibold">{f.name}</div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-70 hover:opacity-100">
                      <button
                        onClick={() => setEdit(f)}
                        className="rounded p-1 text-xs text-muted-foreground hover:bg-muted"
                      >{ht("Éditer")}
                      </button>
                      <button
                        onClick={() => setDel(f)}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
                        title={ht("Supprimer")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showCreate && <FamilleModal onClose={() => setShowCreate(false)} />}
      {edit && <FamilleModal onClose={() => setEdit(null)} initial={edit} />}

      <ConfirmDialog
        open={del !== null}
        title={ht("Supprimer cette famille ?")}
        description={`Cette action supprimera définitivement la famille "${del?.name ?? ""}". Les articles associés perdront leur catégorie. Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => del && deleteMutation.mutate(del.id)}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}
